import type { Ref } from 'react';
import { useCallback, useContext } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type { Sport } from '@sorare/wallet-shared';
import type { SignInWithPasskey } from '@sorare/wallet-shared/src/contexts/messaging';

import apple from '#src/assets/oauth/apple.webp';
import facebook from '#src/assets/oauth/facebook.webp';
import google from '#src/assets/oauth/google.webp';
import passkeys from '#src/assets/oauth/passkeys.svg';
import useInfo from '#src/hooks/useInfo.ts';
import useRequestOAuth from '#src/hooks/useRequestOAuth.ts';

export enum OAuthMethods {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  APPLE = 'apple',
}

type Props = {
  methods?: OAuthMethods[];
  signup: boolean;
  nickname?: string;
  sport?: Sport;
  acceptTerms?: boolean;
  acceptAgeLimit?: boolean;
  acceptToShareAll?: boolean;
  acceptToShareSpecific?: string[];
  ref?: Ref<HTMLDivElement>;
};

export const OAuth = ({
  methods = [OAuthMethods.APPLE, OAuthMethods.FACEBOOK, OAuthMethods.GOOGLE],
  signup,
  nickname,
  sport,
  acceptTerms,
  acceptAgeLimit,
  acceptToShareAll,
  acceptToShareSpecific,
  ref,
}: Props) => {
  const { dict } = useInfo();
  const {
    featureFlags: { usePasskeys },
  } = useInfo();

  const oAuthParams = {
    signup,
    nickname,
    sport,
    acceptTerms,
    acceptAgeLimit,
    acceptToShareAll,
    acceptToShareSpecific,
  };
  const { setUser } = useInfo();
  const requestOAuth = useRequestOAuth();
  const { sendRequest } = useContext(MessagingContext)!;

  const signInWithPasskey = useCallback(async () => {
    const { result, error } = await sendRequest<SignInWithPasskey>(
      'signInWithPasskey',
      {}
    );

    if (error === 'rate-limit') {
      return { email: dict.tooManyLoginAttempts };
    }
    if (error) {
      return { email: error };
    }

    setUser(result);
    return undefined;
  }, [sendRequest, setUser, dict]);

  return (
    <div className="oAuth" ref={ref}>
      <div className="oAuthQuickAccessTitle">{dict.orQuickAccess}</div>
      <div className="oAuthQuickAccess">
        {methods.includes(OAuthMethods.GOOGLE) && (
          <button
            type="button"
            onClick={() => {
              requestOAuth({ platform: 'google', ...oAuthParams });
            }}
          >
            <img src={google} alt="Google" />
          </button>
        )}
        {methods.includes(OAuthMethods.APPLE) && (
          <button
            type="button"
            onClick={() => {
              requestOAuth({ platform: 'apple', ...oAuthParams });
            }}
          >
            <img src={apple} alt="Apple" />
          </button>
        )}
        {methods.includes(OAuthMethods.FACEBOOK) && (
          <button
            type="button"
            onClick={() => {
              requestOAuth({
                platform: 'facebook',
                ...oAuthParams,
              });
            }}
          >
            <img src={facebook} alt="Facebook" />
          </button>
        )}
        {!!usePasskeys && (
          <button type="button" onClick={signInWithPasskey}>
            <img src={passkeys} alt="Passkey" />
          </button>
        )}
      </div>
    </div>
  );
};
